import React, { useState } from 'react';
import { X, Camera, Upload, Keyboard, Receipt as ReceiptIcon, Gauge } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import api from '../services/api';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const CameraMenuModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Type selection, 2: Mileage type, 3: Input method
  const [selectedType, setSelectedType] = useState(null); // 'receipt' or 'mileage'
  const [mileageType, setMileageType] = useState(null); // 'start', 'end', 'since_fill'
  const [inputMethod, setInputMethod] = useState(null); // 'photo', 'upload', 'manual'
  const [uploading, setUploading] = useState(false);

  // Receipt form state
  const [receiptDate, setReceiptDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');

  // Mileage form state
  const [mileageValue, setMileageValue] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));

  const resetModal = () => {
    setStep(1);
    setSelectedType(null);
    setMileageType(null);
    setInputMethod(null);
    setReceiptDate(format(new Date(), 'yyyy-MM-dd'));
    setReceiptAmount('');
    setReceiptNotes('');
    setMileageValue('');
    setSelectedMonth(format(new Date(), 'yyyy-MM'));
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Step 1: Choose Receipt or Mileage
  const handleTypeSelection = (type) => {
    setSelectedType(type);
    if (type === 'receipt') {
      setStep(3); // Skip to input method for receipts
    } else {
      setStep(2); // Go to mileage type selection
    }
  };

  // Step 2: Choose mileage type
  const handleMileageTypeSelection = (type) => {
    setMileageType(type);
    setStep(3); // Go to input method selection
  };

  // Step 3: Choose input method
  const handleInputMethodSelection = (method) => {
    setInputMethod(method);
  };

  // Handle image upload for OCR
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    const file = acceptedFiles[0];

    try {
      if (selectedType === 'receipt') {
        // OCR for receipt
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`${API}/ocr/extract-receipt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        // Use extracted data or allow manual adjustment
        if (response.data.amount) setReceiptAmount(response.data.amount);
        if (response.data.date) setReceiptDate(response.data.date);
        if (response.data.vendor) setReceiptNotes(response.data.vendor);

        // Convert image to base64 and save
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          
          await api.post(`${API}/receipts`, {
            date: response.data.date || receiptDate,
            amount: parseFloat(response.data.amount || receiptAmount || 0),
            notes: response.data.vendor || receiptNotes,
            image_base64: base64,
          });
          
          toast.success('Receipt uploaded successfully!');
          if (onSuccess) onSuccess();
          handleClose();
        };
        reader.readAsDataURL(file);
      } else if (selectedType === 'mileage') {
        // OCR for mileage
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`${API}/ocr/extract-mileage`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const extractedMileage = response.data.start_mileage || response.data.end_mileage || 0;
        
        if (mileageType === 'start' || mileageType === 'end') {
          // Save to monthly mileage
          const monthData = await api.get(`${API}/monthly-mileage/${selectedMonth}`).catch(() => ({ data: null }));
          
          const payload = {
            month: selectedMonth,
            start_mileage: mileageType === 'start' ? extractedMileage : (monthData.data?.start_mileage || extractedMileage),
            end_mileage: mileageType === 'end' ? extractedMileage : (monthData.data?.end_mileage || extractedMileage),
          };
          
          await api.post(`${API}/monthly-mileage`, payload);
          toast.success(`${mileageType === 'start' ? 'Start' : 'End'} mileage saved: ${extractedMileage}`);
        } else if (mileageType === 'since_fill') {
          // Save as "since last fill" - this will update the dashboard card
          await api.post(`${API}/fuel-card`, {
            last_fill_mileage: extractedMileage,
            recorded_at: new Date().toISOString(),
          });
          toast.success(`Mileage since last fill saved: ${extractedMileage}`);
        }
        
        if (onSuccess) onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  // Handle manual input submission
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      if (selectedType === 'receipt') {
        if (!receiptAmount) {
          toast.error('Please enter receipt amount');
          setUploading(false);
          return;
        }

        await api.post(`${API}/receipts`, {
          date: receiptDate,
          amount: parseFloat(receiptAmount),
          notes: receiptNotes,
          image_base64: null, // No image for manual entry
        });
        
        toast.success('Receipt saved successfully!');
      } else if (selectedType === 'mileage') {
        if (!mileageValue) {
          toast.error('Please enter mileage value');
          setUploading(false);
          return;
        }

        if (mileageType === 'start' || mileageType === 'end') {
          const monthData = await api.get(`${API}/monthly-mileage/${selectedMonth}`).catch(() => ({ data: null }));
          
          const payload = {
            month: selectedMonth,
            start_mileage: mileageType === 'start' ? parseFloat(mileageValue) : (monthData.data?.start_mileage || parseFloat(mileageValue)),
            end_mileage: mileageType === 'end' ? parseFloat(mileageValue) : (monthData.data?.end_mileage || parseFloat(mileageValue)),
          };
          
          await api.post(`${API}/monthly-mileage`, payload);
          toast.success(`${mileageType === 'start' ? 'Start' : 'End'} mileage saved!`);
        } else if (mileageType === 'since_fill') {
          await api.post(`${API}/fuel-card`, {
            last_fill_mileage: parseFloat(mileageValue),
            recorded_at: new Date().toISOString(),
          });
          toast.success('Mileage since last fill saved!');
        }
      }

      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.heic', '.heif'] },
    multiple: false,
    disabled: uploading,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">
            {step === 1 && 'Choose Upload Type'}
            {step === 2 && 'Choose Mileage Type'}
            {step === 3 && inputMethod === null && 'Choose Input Method'}
            {step === 3 && inputMethod === 'manual' && selectedType === 'receipt' && 'Enter Receipt Details'}
            {step === 3 && inputMethod === 'manual' && selectedType === 'mileage' && 'Enter Mileage'}
            {step === 3 && (inputMethod === 'photo' || inputMethod === 'upload') && 'Upload Image'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Type Selection */}
        {step === 1 && (
          <div className="space-y-4">
            <button
              onClick={() => handleTypeSelection('receipt')}
              className="w-full p-6 rounded-xl border-2 border-[rgba(20,184,166,0.3)] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-all"
            >
              <ReceiptIcon className="w-12 h-12 text-[#14B8A6] mx-auto mb-3" />
              <p className="text-lg font-semibold">Receipt</p>
              <p className="text-sm text-gray-400 mt-1">Upload fuel receipt</p>
            </button>

            <button
              onClick={() => handleTypeSelection('mileage')}
              className="w-full p-6 rounded-xl border-2 border-[rgba(139,92,246,0.3)] hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.1)] transition-all"
            >
              <Gauge className="w-12 h-12 text-[#8B5CF6] mx-auto mb-3" />
              <p className="text-lg font-semibold">Mileage</p>
              <p className="text-sm text-gray-400 mt-1">Record odometer reading</p>
            </button>
          </div>
        )}

        {/* Step 2: Mileage Type Selection */}
        {step === 2 && (
          <div className="space-y-3">
            <button
              onClick={() => handleMileageTypeSelection('start')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(139,92,246,0.3)] hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.1)] transition-all text-left"
            >
              <p className="font-semibold">Start Mileage</p>
              <p className="text-xs text-gray-400">Mileage at start of month</p>
            </button>

            <button
              onClick={() => handleMileageTypeSelection('end')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(139,92,246,0.3)] hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.1)] transition-all text-left"
            >
              <p className="font-semibold">End Mileage</p>
              <p className="text-xs text-gray-400">Mileage at end of month</p>
            </button>

            <button
              onClick={() => handleMileageTypeSelection('since_fill')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(139,92,246,0.3)] hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.1)] transition-all text-left"
            >
              <p className="font-semibold">Mileage Since Last Fuelling Up</p>
              <p className="text-xs text-gray-400">Updates dashboard card</p>
            </button>
          </div>
        )}

        {/* Step 3: Input Method Selection */}
        {step === 3 && inputMethod === null && (
          <div className="space-y-3">
            <button
              onClick={() => handleInputMethodSelection('photo')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(20,184,166,0.3)] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-all"
            >
              <Camera className="w-8 h-8 text-[#14B8A6] mx-auto mb-2" />
              <p className="font-semibold">Take Photo</p>
              <p className="text-xs text-gray-400">Use camera</p>
            </button>

            <button
              onClick={() => handleInputMethodSelection('upload')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(20,184,166,0.3)] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-all"
            >
              <Upload className="w-8 h-8 text-[#14B8A6] mx-auto mb-2" />
              <p className="font-semibold">Upload Photo</p>
              <p className="text-xs text-gray-400">From gallery</p>
            </button>

            <button
              onClick={() => handleInputMethodSelection('manual')}
              className="w-full p-4 rounded-lg border-2 border-[rgba(20,184,166,0.3)] hover:border-[#14B8A6] hover:bg-[rgba(20,184,166,0.1)] transition-all"
            >
              <Keyboard className="w-8 h-8 text-[#14B8A6] mx-auto mb-2" />
              <p className="font-semibold">Input Manually</p>
              <p className="text-xs text-gray-400">Type values</p>
            </button>
          </div>
        )}

        {/* Step 3: Photo/Upload Interface */}
        {step === 3 && (inputMethod === 'photo' || inputMethod === 'upload') && (
          <div>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-8 transition-colors cursor-pointer ${
                isDragActive
                  ? 'border-[#14B8A6] bg-[rgba(20,184,166,0.1)]'
                  : 'border-[rgba(20,184,166,0.3)]'
              }`}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="text-center">
                  <div className="animate-spin w-12 h-12 border-4 border-[#14B8A6] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-300">Processing...</p>
                </div>
              ) : (
                <div className="text-center">
                  {inputMethod === 'photo' ? (
                    <>
                      <Camera className="w-16 h-16 text-[#14B8A6] mx-auto mb-4" />
                      <p className="text-lg font-medium mb-2">Take a photo</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-16 h-16 text-[#14B8A6] mx-auto mb-4" />
                      <p className="text-lg font-medium mb-2">Upload photo</p>
                    </>
                  )}
                  <p className="text-sm text-gray-400">
                    {isDragActive ? 'Drop here' : 'Click or drag'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Manual Input - Receipt */}
        {step === 3 && inputMethod === 'manual' && selectedType === 'receipt' && (
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                className="glass-input w-full text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Amount (£)</label>
              <input
                type="number"
                step="0.01"
                value={receiptAmount}
                onChange={(e) => setReceiptAmount(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="45.50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <input
                type="text"
                value={receiptNotes}
                onChange={(e) => setReceiptNotes(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="Shell station"
              />
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="btn-primary w-full text-sm"
            >
              {uploading ? 'Saving...' : 'Save Receipt'}
            </button>
          </form>
        )}

        {/* Step 3: Manual Input - Mileage */}
        {step === 3 && inputMethod === 'manual' && selectedType === 'mileage' && (
          <form onSubmit={handleManualSubmit} className="space-y-4">
            {(mileageType === 'start' || mileageType === 'end') && (
              <div>
                <label className="block text-sm font-medium mb-2">Select Month</label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="glass-input w-full text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">
                {mileageType === 'start' && 'Start Mileage'}
                {mileageType === 'end' && 'End Mileage'}
                {mileageType === 'since_fill' && 'Current Mileage'}
              </label>
              <input
                type="number"
                step="0.1"
                value={mileageValue}
                onChange={(e) => setMileageValue(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="Enter mileage"
                required
              />
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="btn-primary w-full text-sm"
            >
              {uploading ? 'Saving...' : 'Save Mileage'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CameraMenuModal;
